import { Component, computed, inject, input, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../design-system/form-field.component';
import { InfoHintComponent } from '../design-system/info-hint.component';
import { AinStateComponent } from '../design-system/state.component';
import { StructuredJsonFormComponent } from '../design-system/structured-json-form.component';
import { StructuredJsonFieldDef } from '../design-system/structured-json-form.model';
import {
  parseStructuredJsonPayload,
  serializeStructuredJsonPayload
} from '../design-system/structured-json-form.utils';
import {
  AiAssistAppliedEvent,
  AiAssistContextSelections,
  AiAssistDiscardFn,
  AiAssistFieldPatch,
  AiAssistGenerateFn,
  AiAssistGenerateRequest,
  AiAssistMode,
  AiAssistPreviewPatchRow,
  AiAssistResult,
  AiAssistStep
} from './ai-assist.models';
import {
  hasAiAssistPermission,
  resolveAiAssistError,
  truncateAiAssistPreview
} from './ai-assist.util';

@Component({
  standalone: true,
  selector: 'ain-ai-assist-shell',
  imports: [
    FormsModule,
    ButtonModule,
    CheckboxModule,
    DialogModule,
    TagModule,
    TextareaModule,
    AinFormFieldComponent,
    InfoHintComponent,
    AinStateComponent,
    StructuredJsonFormComponent
  ],
  providers: [MessageService],
  template: `
    <p-dialog
      [header]="resolvedTitle()"
      [modal]="true"
      [visible]="visible()"
      (visibleChange)="onVisibleChange($event)"
      [closable]="!generating()"
      [closeOnEscape]="!generating()"
      [dismissableMask]="!generating()"
      [style]="{ width: step() === 'preview' ? 'min(960px, 96vw)' : 'min(720px, 94vw)' }"
      [draggable]="false"
      (onHide)="onDialogHide()">
      @if (step() === 'prompt') {
        <div class="shell-body">
          @if (infoTitleKey()) {
            <ain-info-hint
              [titleKey]="infoTitleKey()"
              [descriptionKey]="infoDescriptionKey()"
              [bulletKeys]="infoBulletKeys()" />
          }

          <ng-content select="[aiAssistContext]" />

          @if (structuredFieldDefs().length) {
            <section class="structured-block">
              <ain-structured-json-form
                [fields]="structuredFieldDefs()"
                [payloadJson]="structuredAnswersJson"
                (payloadJsonChange)="onStructuredAnswersJsonChange($event)" />
            </section>
          }

          @if (showPromptTextarea()) {
            <ain-form-field
              [label]="t(promptLabelKey())"
              [hint]="promptHintKey() ? t(promptHintKey()) : ''">
              <textarea
                pTextarea
                rows="5"
                [ngModel]="instruction()"
                (ngModelChange)="instruction.set($event ?? '')"
                [placeholder]="promptPlaceholderKey() ? t(promptPlaceholderKey()) : ''"
                [maxlength]="8000"></textarea>
            </ain-form-field>
          }

          @if (errorMessage()) {
            <div class="error-panel">
              <ain-state
                type="error"
                [title]="t('shared.aiAssist.generateFailed')"
                [message]="errorMessage() ?? ''" />
              <button
                pButton
                type="button"
                severity="secondary"
                [label]="t('shared.aiAssist.retry')"
                (click)="runGenerate()"></button>
            </div>
          }
        </div>
      } @else if (step() === 'loading') {
        <ain-state
          type="loading"
          [title]="t('shared.aiAssist.loading')"
          [message]="t('shared.aiAssist.loadingHint')" />
      } @else if (step() === 'preview') {
        <div class="shell-body">
          @if (infoPreviewTitleKey()) {
            <ain-info-hint
              [titleKey]="infoPreviewTitleKey()"
              [descriptionKey]="infoPreviewDescriptionKey()"
              [bulletKeys]="infoPreviewBulletKeys()" />
          }

          @if (usedFallback()) {
            <div class="banner banner-warn">
              <p-tag severity="warn" [value]="t('shared.aiAssist.fallback')" />
              <span>{{ t('shared.aiAssist.fallbackHint') }}</span>
            </div>
          }

          @if (warnings().length) {
            <div class="banner banner-info">
              <strong>{{ t('shared.aiAssist.warningsTitle') }}</strong>
              <ul>
                @for (warning of warnings(); track warning) {
                  <li>{{ warning }}</li>
                }
              </ul>
            </div>
          }

          <ng-content select="[aiAssistPreviewExtra]" />

          <section class="patch-section">
            <div class="patch-header">
              <h4>{{ t('shared.aiAssist.selectFields') }}</h4>
              @if (previewRows().length) {
                <button
                  pButton
                  type="button"
                  [text]="true"
                  size="small"
                  [label]="allSelected() ? t('shared.aiAssist.deselectAll') : t('shared.aiAssist.selectAll')"
                  (click)="toggleSelectAll()"></button>
              }
            </div>

            @if (!previewRows().length) {
              <ain-state type="empty" [title]="t('shared.aiAssist.emptyPatch')" />
            } @else {
              <ul class="patch-list">
                @for (row of previewRows(); track row.fieldKey; let i = $index) {
                  <li class="patch-item">
                    <p-checkbox
                      [binary]="true"
                      [ngModel]="row.selected"
                      (ngModelChange)="setRowSelected(i, $event)" />
                    <div class="patch-body">
                      <div class="patch-label">{{ row.displayLabel }}</div>
                      <div class="patch-diff">
                        <div class="diff-col">
                          <span class="diff-caption">{{ t('shared.aiAssist.before') }}</span>
                          <p>{{ row.beforePreview }}</p>
                        </div>
                        <div class="diff-col after">
                          <span class="diff-caption">{{ t('shared.aiAssist.after') }}</span>
                          <p>{{ row.afterPreview }}</p>
                        </div>
                      </div>
                    </div>
                  </li>
                }
              </ul>
            }
          </section>
        </div>
      }

      <ng-template pTemplate="footer">
        @if (step() === 'prompt') {
          <button
            pButton
            type="button"
            severity="secondary"
            [label]="t('shared.aiAssist.cancel')"
            [disabled]="generating()"
            (click)="close()"></button>
          <button
            pButton
            type="button"
            icon="pi pi-sparkles"
            [label]="t('shared.aiAssist.generate')"
            [disabled]="!canSubmitGenerate()"
            (click)="runGenerate()"></button>
        } @else if (step() === 'loading') {
          <button
            pButton
            type="button"
            severity="secondary"
            [label]="t('shared.aiAssist.cancel')"
            [disabled]="true"></button>
        } @else if (step() === 'preview') {
          <button
            pButton
            type="button"
            severity="secondary"
            [label]="t('shared.aiAssist.back')"
            [disabled]="discarding()"
            (click)="backToPrompt()"></button>
          @if (canDiscard()) {
            <button
              pButton
              type="button"
              severity="danger"
              [label]="t('shared.aiAssist.discard')"
              [disabled]="discarding()"
              (click)="runDiscard()"></button>
          }
          <button
            pButton
            type="button"
            icon="pi pi-check"
            [label]="t('shared.aiAssist.apply')"
            [disabled]="discarding() || !hasSelectedPatches()"
            (click)="applySelected()"></button>
        }
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .shell-body { display: grid; gap: 14px; }
    .structured-block { display: grid; gap: 8px; }
    .error-panel { display: grid; gap: 8px; justify-items: start; }
    .banner {
      display: flex;
      gap: 10px;
      align-items: flex-start;
      flex-wrap: wrap;
      border-radius: 10px;
      padding: 10px 12px;
      font-size: 0.9rem;
      line-height: 1.5;
    }
    .banner-warn { background: #fffbeb; border: 1px solid #fcd34d; color: #92400e; }
    .banner-info { background: #eff6ff; border: 1px solid #bfdbfe; color: #1e3a8a; }
    .banner-info ul { margin: 6px 0 0; padding-inline-start: 18px; }
    .patch-section { display: grid; gap: 10px; }
    .patch-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
    }
    .patch-header h4 { margin: 0; font-size: 0.95rem; }
    .patch-list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: grid;
      gap: 10px;
    }
    .patch-item {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 12px;
      align-items: start;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px;
      background: #fff;
    }
    .patch-label { font-weight: 600; color: #111827; margin-bottom: 8px; }
    .patch-diff {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
    }
    .diff-col {
      background: #f8fafc;
      border-radius: 8px;
      padding: 8px 10px;
      min-width: 0;
    }
    .diff-col.after { background: #ecfdf5; }
    .diff-caption {
      display: block;
      font-size: 0.75rem;
      font-weight: 600;
      color: #64748b;
      margin-bottom: 4px;
      text-transform: uppercase;
      letter-spacing: 0.02em;
    }
    .diff-col p {
      margin: 0;
      color: #334155;
      font-size: 0.875rem;
      white-space: pre-wrap;
      word-break: break-word;
    }
    @media (max-width: 640px) {
      .patch-diff { grid-template-columns: 1fr; }
    }
  `]
})
export class AinAiAssistShellComponent {
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);
  private readonly contextService = inject(CurrentContextService);

  /** Two-way dialog visibility. Prefer `open()` / `close()` from hosts. */
  readonly visible = model(false);

  readonly titleKey = input('shared.aiAssist.title');
  readonly mode = input<AiAssistMode>('create');

  readonly infoTitleKey = input('');
  readonly infoDescriptionKey = input('');
  readonly infoBulletKeys = input<string[]>([]);

  readonly infoPreviewTitleKey = input('');
  readonly infoPreviewDescriptionKey = input('');
  readonly infoPreviewBulletKeys = input<string[]>([]);

  readonly structuredFieldDefs = input<StructuredJsonFieldDef[]>([]);
  /** Structured intake answers (host-bound object). */
  readonly structuredAnswers = model<Record<string, unknown>>({});

  /** Free-form instruction / prompt text. */
  readonly instruction = model('');
  readonly showPromptTextarea = input(true);
  readonly promptLabelKey = input('shared.aiAssist.promptLabel');
  readonly promptHintKey = input('shared.aiAssist.promptHint');
  readonly promptPlaceholderKey = input('shared.aiAssist.promptPlaceholder');
  /** When true, Generate requires a non-empty instruction. Defaults by mode. */
  readonly requireInstruction = input<boolean | null>(null);

  /** Opaque context selection bag — never interpreted by the shell. */
  readonly contextSelections = model<AiAssistContextSelections>({});
  readonly sourceHash = input<string | null>(null);

  /**
   * Host-provided generate callback. Shell stays free of Growth/domain services.
   * `(payload) => Observable<AiAssistResult>`
   */
  readonly generate = input<AiAssistGenerateFn | null>(null);

  /** Optional discard callback; footer Discard shows when draftId + discard are set. */
  readonly discard = input<AiAssistDiscardFn | null>(null);

  /** Optional permission gate for `open()`. */
  readonly permissionKey = input<string | null>(null);
  readonly permissionAction = input<string | null>(null);

  readonly applied = output<AiAssistAppliedEvent>();
  readonly closed = output<void>();

  readonly step = signal<AiAssistStep>('prompt');
  readonly generating = signal(false);
  readonly discarding = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly warnings = signal<string[]>([]);
  readonly usedFallback = signal(false);
  readonly draftId = signal<string | null>(null);
  readonly previewRows = signal<AiAssistPreviewPatchRow[]>([]);
  readonly lastResult = signal<AiAssistResult | null>(null);

  /** Bridge for ain-structured-json-form string payload. */
  structuredAnswersJson = '{}';

  readonly resolvedTitle = computed(() => this.t(this.titleKey()));

  readonly allSelected = computed(() => {
    const rows = this.previewRows();
    return rows.length > 0 && rows.every(row => row.selected);
  });

  open(options?: {
    instruction?: string;
    structuredAnswers?: Record<string, unknown>;
    contextSelections?: AiAssistContextSelections;
  }): void {
    if (!this.canOpenByPermission()) {
      this.messages.add({ severity: 'warn', summary: this.t('common.noPermission') });
      return;
    }

    if (options?.instruction !== undefined) {
      this.instruction.set(options.instruction);
    }
    if (options?.structuredAnswers) {
      this.structuredAnswers.set({ ...options.structuredAnswers });
      this.structuredAnswersJson = serializeStructuredJsonPayload(options.structuredAnswers);
    } else {
      this.syncStructuredAnswersJsonFromModel();
    }
    if (options?.contextSelections) {
      this.contextSelections.set({ ...options.contextSelections });
    }

    this.resetTransientState();
    this.visible.set(true);
  }

  close(): void {
    if (this.generating()) {
      return;
    }
    this.visible.set(false);
  }

  onVisibleChange(value: boolean): void {
    if (!value && this.generating()) {
      return;
    }
    this.visible.set(value);
    if (!value) {
      this.resetWizard();
      this.closed.emit();
    }
  }

  onDialogHide(): void {
    if (this.generating()) {
      return;
    }
    this.resetWizard();
  }

  backToPrompt(): void {
    this.step.set('prompt');
    this.errorMessage.set(null);
  }

  onStructuredAnswersJsonChange(json: string): void {
    this.structuredAnswersJson = json;
    this.structuredAnswers.set(parseStructuredJsonPayload(json));
  }

  canSubmitGenerate(): boolean {
    if (this.generating() || !this.generate()) {
      return false;
    }
    if (this.isInstructionRequired() && !this.instruction().trim()) {
      return false;
    }
    return true;
  }

  runGenerate(): void {
    const generateFn = this.generate();
    if (!generateFn) {
      this.messages.add({
        severity: 'warn',
        summary: this.t('shared.aiAssist.errors.generate_fn_missing')
      });
      return;
    }

    if (this.isInstructionRequired() && !this.instruction().trim()) {
      this.messages.add({
        severity: 'warn',
        summary: this.t('shared.aiAssist.errors.prompt_required')
      });
      return;
    }

    const payload: AiAssistGenerateRequest = {
      mode: this.mode(),
      instruction: this.instruction().trim(),
      structuredAnswers: { ...this.structuredAnswers() },
      contextSelections: { ...this.contextSelections() },
      sourceHash: this.sourceHash()
    };

    this.generating.set(true);
    this.errorMessage.set(null);
    this.step.set('loading');

    generateFn(payload)
      .pipe(finalize(() => this.generating.set(false)))
      .subscribe({
        next: result => this.handleGenerateSuccess(result),
        error: (error: unknown) => {
          this.step.set('prompt');
          this.errorMessage.set(resolveAiAssistError(error, key => this.t(key)));
        }
      });
  }

  canDiscard(): boolean {
    return !!this.draftId() && !!this.discard();
  }

  runDiscard(): void {
    const id = this.draftId();
    const discardFn = this.discard();
    if (!id || !discardFn) {
      this.backToPrompt();
      return;
    }

    this.discarding.set(true);
    discardFn(id)
      .pipe(finalize(() => this.discarding.set(false)))
      .subscribe({
        next: () => {
          this.messages.add({ severity: 'success', summary: this.t('shared.aiAssist.discardSuccess') });
          this.draftId.set(null);
          this.previewRows.set([]);
          this.lastResult.set(null);
          this.backToPrompt();
        },
        error: (error: unknown) => {
          this.messages.add({
            severity: 'error',
            summary: resolveAiAssistError(error, key => this.t(key))
          });
        }
      });
  }

  setRowSelected(index: number, selected: boolean): void {
    this.previewRows.update(rows =>
      rows.map((row, i) => (i === index ? { ...row, selected } : row))
    );
  }

  toggleSelectAll(): void {
    const next = !this.allSelected();
    this.previewRows.update(rows => rows.map(row => ({ ...row, selected: next })));
  }

  hasSelectedPatches(): boolean {
    return this.previewRows().some(row => row.selected);
  }

  applySelected(): void {
    const result = this.lastResult();
    const selected: AiAssistFieldPatch[] = this.previewRows()
      .filter(row => row.selected)
      .map(row => ({
        fieldKey: row.fieldKey,
        labelKey: row.labelKey,
        before: row.before,
        after: row.after
      }));

    if (!selected.length) {
      this.messages.add({
        severity: 'warn',
        summary: this.t('shared.aiAssist.errors.no_fields_selected')
      });
      return;
    }

    this.applied.emit({
      draftId: this.draftId(),
      patches: selected,
      result: result ?? { patches: selected, draftId: this.draftId() }
    });
    this.messages.add({ severity: 'success', summary: this.t('shared.aiAssist.applySuccess') });
    this.close();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private handleGenerateSuccess(result: AiAssistResult): void {
    this.lastResult.set(result);
    this.warnings.set(result.warnings ?? []);
    this.usedFallback.set(!!result.usedFallback);
    this.draftId.set(result.draftId ?? null);
    this.previewRows.set((result.patches ?? []).map(patch => this.toPreviewRow(patch)));
    this.step.set('preview');
  }

  private toPreviewRow(patch: AiAssistFieldPatch): AiAssistPreviewPatchRow {
    return {
      ...patch,
      selected: true,
      displayLabel: this.resolvePatchLabel(patch),
      beforePreview: truncateAiAssistPreview(patch.before),
      afterPreview: truncateAiAssistPreview(patch.after)
    };
  }

  private resolvePatchLabel(patch: AiAssistFieldPatch): string {
    if (patch.labelKey?.trim()) {
      const translated = this.t(patch.labelKey);
      if (translated !== patch.labelKey) {
        return translated;
      }
    }

    const def = this.structuredFieldDefs().find(field => field.key === patch.fieldKey);
    if (def?.labelKey) {
      const translated = this.t(def.labelKey);
      if (translated !== def.labelKey) {
        return translated;
      }
    }

    return patch.fieldKey;
  }

  private isInstructionRequired(): boolean {
    const explicit = this.requireInstruction();
    if (explicit !== null && explicit !== undefined) {
      return explicit;
    }
    // Create flows typically rely on structured answers; edit requires a free prompt.
    return this.mode() === 'edit';
  }

  private canOpenByPermission(): boolean {
    const key = this.permissionKey();
    if (!key?.trim()) {
      return true;
    }
    return hasAiAssistPermission(
      this.contextService.context(),
      key,
      this.permissionAction()
    );
  }

  private syncStructuredAnswersJsonFromModel(): void {
    this.structuredAnswersJson = serializeStructuredJsonPayload(this.structuredAnswers() ?? {});
  }

  private resetTransientState(): void {
    this.step.set('prompt');
    this.generating.set(false);
    this.discarding.set(false);
    this.errorMessage.set(null);
    this.warnings.set([]);
    this.usedFallback.set(false);
    this.draftId.set(null);
    this.previewRows.set([]);
    this.lastResult.set(null);
  }

  private resetWizard(): void {
    this.resetTransientState();
  }
}
